import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@/store';
import { removeError } from '@/store/slices/errorSlice';
import type { RequestErrorDetails } from '@/services/requestError';

const GRAPHQL_HIDE_MS = 6000;
const BACKEND_HIDE_MS = 15000;

interface StructuredError {
  message?: string;
  details?: RequestErrorDetails;
}

const getStructuredDetails = (raw: unknown): RequestErrorDetails | undefined => {
  const err = raw as StructuredError | undefined;
  return err?.details?.kind ? err.details : undefined;
};

const titleFor = (details: RequestErrorDetails): string => {
  switch (details.kind) {
    case 'network':
      return `${details.endpoint} unreachable`;
    case 'http':
      return `${details.endpoint} HTTP ${details.status ?? ''}`.trim();
    case 'wsapi-auth-rejected':
      return `${details.endpoint} rejected a fresh token`;
    case 'graphql':
      return `${details.endpoint} GraphQL error`;
    case 'session-expired':
      return 'Session expired';
  }
};

export const GlobalErrorDisplay: React.FC = () => {
  const dispatch = useAppDispatch();
  const { lastError } = useAppSelector((state) => state.error);
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (lastError) {
      setOpen(true);
      setDetailsOpen(false);
    }
  }, [lastError]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
    setTimeout(() => {
      if (lastError) dispatch(removeError(lastError.id));
    }, 200);
  };

  if (!lastError) return null;

  const details = getStructuredDetails(lastError.details);
  const autoHideDuration = !details || details.kind === 'graphql' ? GRAPHQL_HIDE_MS : BACKEND_HIDE_MS;

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        onClose={handleClose}
        severity={lastError.type}
        variant="filled"
        sx={{ width: '100%', maxWidth: 640 }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            {details && (
              <IconButton
                size="small"
                aria-label={detailsOpen ? 'hide details' : 'show details'}
                color="inherit"
                onClick={() => setDetailsOpen((v) => !v)}
              >
                {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        {details && <AlertTitle sx={{ mb: 0 }}>{titleFor(details)}</AlertTitle>}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {lastError.message}
        </Typography>
        {details && (
          <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.25)',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {`kind:      ${details.kind}
endpoint:  ${details.endpoint}
url:       ${details.url}${details.operation ? `
operation: ${details.operation}` : ''}${details.status !== undefined ? `
status:    ${details.status}` : ''}${details.graphqlErrors?.length ? `
graphql:
  - ${details.graphqlErrors.map((e) => e.message).join('\n  - ')}` : ''}${details.bodySnippet ? `
body:
${details.bodySnippet}` : ''}`}
            </Box>
          </Collapse>
        )}
      </Alert>
    </Snackbar>
  );
};
